/* eslint-disable react-hooks/immutability */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * This is the starting point of your application.
 * oneAudi OS and Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Logger } from '@feature-hub/core';
import type {
  TrackingService,
  ContentServiceV1,
  VueFormatterServiceInterfaceV1,
  LocaleServiceV1,
  GfaServiceConfigProviderV1,
} from './feature-service-types';
import { useInViewEffect } from '@oneaudi/feature-app-utils';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import MinimalisticTypographicTeaser from './components/MinimalisticTypographicTeaser';
import { ContentContext } from './context';
import { getCtasForTracking } from '../helper/tracking';
import { getThemeName } from '../alpha-helper';
import { useWltpComplianceGate } from './useWltpComplianceGate';
import { fetchCaeEnforcement } from './marketCompliance';

interface FeatureAppProps {
  trackingService?: TrackingService;
  content?: any;
  contentService?: ContentServiceV1 | undefined;
  loggerService?: Logger;
  vueFormatterService?: VueFormatterServiceInterfaceV1;
  localeService?: LocaleServiceV1;
  isMarketComplianceEnabled?: boolean;
  initialWltpCompliance?: boolean;
  initialWltpLogs?: string[];
  gfaServiceConfigProvider?: GfaServiceConfigProviderV1;
}

const FeatureApp: React.FC<FeatureAppProps> = ({ ...inherited }: FeatureAppProps) => {
  const {
    localeService,
    trackingService: trackingServiceInherited,
    isMarketComplianceEnabled,
    initialWltpCompliance,
    initialWltpLogs,
    gfaServiceConfigProvider,
    loggerService,
  } = inherited;
  const { content } = useContext(ContentContext);

  const [complianceEnabled, setComplianceEnabled] = useState<boolean>(
    isMarketComplianceEnabled ?? false,
  );
  const hasReplayedInitialWltpLogs = useRef(false);

  useEffect(() => {
    if (hasReplayedInitialWltpLogs.current) return;
    if (!Array.isArray(initialWltpLogs) || !initialWltpLogs.length) return;

    initialWltpLogs.forEach((log) => {
      console.error(log);
    });

    hasReplayedInitialWltpLogs.current = true;
  }, [initialWltpLogs]);

  const isCompliant = useWltpComplianceGate({
    isMarketComplianceEnabled: complianceEnabled,
    initialCompliance: initialWltpCompliance,
    logger: loggerService,
  });

  const trackingService = useMemo(
    () => (typeof window !== 'undefined' ? trackingServiceInherited! : ({} as TrackingService)),
    [trackingServiceInherited],
  );

  useEffect(() => {
    trackingService.featureAppName = 'minimalistic-typographic-teaser';
    const readyEvent = {
      event: {
        attributes: {
          componentName: 'minimalistic-typographic-teaser',
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: 'minimalistic typographic teaser - feature app ready',
        },
      },
    };
    trackingService.track(readyEvent);
  }, [trackingService]);

  useInViewEffect(() => {
    const readyEvent = {
      event: {
        attributes: {
          componentName: 'minimalistic-typographic-teaser',
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
          relatedElements: getCtasForTracking(content?.sectionHeaderCTAs),
        },
        eventInfo: {
          eventAction: 'impression',
          eventName: 'minimalistic typographic teaser - impression',
        },
      },
    };
    trackingService.track(readyEvent);
  });

  useEffect(() => {
    if (isMarketComplianceEnabled !== undefined) return;

    let cancelled = false;
    fetchCaeEnforcement({ gfaServiceConfigProvider })
      .then((result) => {
        if (!cancelled) setComplianceEnabled(result.caeDataRequired);
      })
      .catch(() => {
        // Error fetching CAE enforcement
      });
    return () => {
      cancelled = true;
    };
  }, [gfaServiceConfigProvider, isMarketComplianceEnabled]);

  const sectionHeaderCTAs = content?.sectionHeaderCTAs ?? {};

  const sectionHeaderContent = content?.sectionHeaderContent ?? {};

  const { headline, subhead, copy } = sectionHeaderContent as {
    headline?: string;
    subhead?: string;
    copy?: string;
  };

  const { cta1 = {}, cta2 = {} } = sectionHeaderCTAs as {
    cta1?: { text?: string };
    cta2?: { text?: string };
  };

  const cta1Text = cta1.text ?? '';
  const cta2Text = cta2.text ?? '';

  if (!isCompliant) {
    return null;
  }

  if (!headline && !subhead && !copy && !cta1Text && !cta2Text) {
    return null;
  }

  return (
    <ThemeProvider
      theme={{ name: getThemeName(content.themeContent.theme === 'darkTheme' ? 'dark' : 'light') }}
    >
      <MinimalisticTypographicTeaser
        content={content}
        localeService={localeService}
        trackingService={trackingService}
      />
    </ThemeProvider>
  );
};

export default FeatureApp;
